import { memo, useMemo } from 'react';
import styled, { css, useTheme, DefaultTheme } from 'styled-components';
import { isFunction, memoizeFunction } from '@agilepoint/utils-common';
import { Typography } from '../../typography';
import { Icon } from '../../icon';
import { ITreeItemProps, ITreeViewRenderItemProps } from '../../tree-view';
import { StackPanel } from '../../stack-panel';
import { Badge } from '../../badge';
import { FlexBox } from '../../flex-box';
import { extractNumberAsString } from '../../../utils';
import { MenuType, MenuState } from '../theme';
import { CursorType } from '../common';
import { selectThemeSemantics } from '../../../styling';

const StyledMenuIcon = styled(Icon).attrs({
    iconName: 'ap-right-arrow-thin2.outline'
}) <{ $isExpanded: boolean; }>`
    transform-origin: center;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? '-90deg' : '90deg')});
    transition: all 0.15s ease-in-out;
`;

const labelStyle = css`
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const RegularLabel = styled(Typography.Medium)`
    ${labelStyle}
`;

const Header = styled(Typography.H5)`
    ${labelStyle}
`;

const getThemeColorGetter = (type: MenuType, state: MenuState) => (theme: DefaultTheme) => theme.menu[type].colors.item[state];

const getMenuThemeStyles = memoizeFunction((type: MenuType, state: MenuState) => {
    const getThemeProperties = getThemeColorGetter(type, state);
    return css`
        border-bottom: 1px solid ${({ theme }) => getThemeProperties(theme).borderColor};
        background-color: ${({ theme }) => getThemeProperties(theme).backgroundColor};
        color: ${({ theme }) => getThemeProperties(theme).text};

        ${Icon} {
            fill: ${({ theme }) => getThemeProperties(theme).mainIcon};
        }

        ${StyledMenuIcon} {
            fill: ${({ theme }) => getThemeProperties(theme).menuIcon};
        }
    `;
});

interface IItemWrapperProps {
    $cursorType?: CursorType;
    $isInCollapsedMode: boolean;
    $isSelected?: boolean;
    $isSelectable?: boolean;
    $shouldHighlightItem?: boolean;
}

const ItemWrapper = styled(FlexBox).attrs({
    className: String('ap-collapsibleMenu-itemWrapper')
}) <IItemWrapperProps>`
    border-style: solid;
    transition: all 0.15s ease-in-out;
    cursor: ${({ $cursorType = 'default' }) => $cursorType};
    border-radius: ${({ theme: { menu: { collapsibleMenu: { composites } } } }) => composites.borderRadius.item};
    border-width: ${({ theme: { menu: { collapsibleMenu: { composites } } } }) => composites.borderWidth.item};
    margin: ${selectThemeSemantics(({ gap }) => `${gap.l} ${gap.none}`)};
    padding: ${selectThemeSemantics(({ gap }) => `${gap.l} ${gap.xl}`)};

    ${({ $shouldHighlightItem, $isSelected }) => getMenuThemeStyles('collapsibleMenu', $isSelected ? 'selected' : ($shouldHighlightItem ? 'expanded' : 'default'))}

    :hover {
        ${({ $isSelectable, $shouldHighlightItem, $isSelected }) => ($isSelectable && !$shouldHighlightItem && !$isSelected && getMenuThemeStyles('collapsibleMenu', 'hovered'))}
    }

    :focus {
        outline: inherit;
        box-shadow: 0px 0px 4px ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    }
`;

interface ICollapsibleMenuItem extends ITreeViewRenderItemProps, Pick<ITreeItemProps, 'onRenderContent'> {
    cursorType?: CursorType;
    triggerChangeOnParentSelection?: boolean;
}

export const CollapsibleMenuItem = memo((props: ICollapsibleMenuItem) => {
    const {
        item, hasChildren, shouldHighlightItem, triggerChangeOnParentSelection, isInCompactMode,
        isExpanded, onClick, cursorType, onRenderContent, isLoading
    } = props;
    const { additionalInfo, iconName, iconSize, label } = item;
    const Label = useMemo(() => (hasChildren ? Header : RegularLabel), [hasChildren]);
    const { semantics: { gap: { xl } } } = useTheme();
    const gap = useMemo(() => extractNumberAsString(xl), [xl]);
    const shouldRenderAllContent = !isInCompactMode;

    const _renderContent = () => (
        isFunction(onRenderContent) ? onRenderContent({ isInCompactMode, item, isLoading })
            : (
                <StackPanel title={label} alignment="center" gap={gap}>
                    <Icon className="ap-collapsibleMenu-itemIcon" iconName={iconName!} iconSize={iconSize} hidden={!iconName} />
                    <Label className="ap-collapsibleMenu-itemLabel" hidden={!(shouldRenderAllContent && label)}>{label}</Label>
                </StackPanel>
            )
    );

    return (
        <ItemWrapper
            tabIndex={isInCompactMode ? -1 : 0}
            alignItems="center"
            justifyContent="space-between"
            $cursorType={cursorType}
            $shouldHighlightItem={shouldHighlightItem}
            $isSelected={shouldHighlightItem && !hasChildren}
            $isSelectable={triggerChangeOnParentSelection || !hasChildren}
            $isInCollapsedMode={isInCompactMode}
            onClick={onClick}>
            {_renderContent()}
            {
                shouldRenderAllContent && (
                    <StackPanel alignment="center" gap={gap}>
                        <Badge className="ap-collapsibleMenu-itemBadge" label={additionalInfo!} hidden={!additionalInfo} />
                        <StyledMenuIcon className="ap-collapsibleMenu-expandIcon" $isExpanded={isExpanded} iconSize="16" hidden={!hasChildren} />
                    </StackPanel>
                )
            }
        </ItemWrapper>
    );
});

CollapsibleMenuItem.displayName = 'CollapsibleMenuItem';
